import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { fetchOptionsCampaignDetail, fetchOptionsCampaigns, fetchOptionsOperationDetail, fetchOptionsOperations, syncOptionsAccount } from '../lib/optionsApi';
import { buildOpenPositionsStrategyOptions, computeDteDays, filterOpenPositions, formatSizeDisplay, getCampaignContext, sortOpenPositions } from '../components/options/optionsOpenPositionsUtils';
import { useOptionsLocale } from '../context/OptionsLanguageContext';
import { emitOptionsOpenOperationsUpdated } from '../lib/optionsEvents';

export const useOptionsOpenPositionsData = (connected, reloadOverview) => {
  const { t } = useOptionsLocale();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('usd');
  const [underlyingFilter, setUnderlyingFilter] = useState('all');
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [sortBy, setSortBy] = useState('dte');
  const [items, setItems] = useState([]);

  const loadOpenPositions = useCallback(async () => {
    if (!connected) {
      setItems([]);
      return;
    }

    setLoading(true);
    try {
      const [operationsResponse, campaignsResponse] = await Promise.all([
        fetchOptionsOperations(200),
        fetchOptionsCampaigns(200),
      ]);

      const openOperations = (operationsResponse.items || []).filter((item) => item.status !== 'CLOSED');
      const openOperationDetails = await Promise.all(openOperations.map((operation) => fetchOptionsOperationDetail(operation.id)));

      const relevantCampaigns = (campaignsResponse.items || []).filter((campaign) =>
        openOperations.some((operation) => (campaign.operation_ids || []).includes(operation.id))
      );
      const uniqueCampaigns = Array.from(new Map(relevantCampaigns.map((campaign) => [campaign.id, campaign])).values());
      const campaignDetailsArray = await Promise.all(uniqueCampaigns.map((campaign) => fetchOptionsCampaignDetail(campaign.id)));
      const campaignByOperationId = new Map();

      campaignDetailsArray.forEach((detail) => {
        (detail.operations || []).forEach((operation) => {
          campaignByOperationId.set(operation.id, detail);
        });
      });

      const normalized = openOperationDetails.map((detail) => {
        const operation = detail.operation;
        const legs = detail.legs || [];
        const dte = computeDteDays(operation.expiration);
        const campaignDetail = campaignByOperationId.get(operation.id) || null;
        const campaignContext = getCampaignContext({ operation, campaignDetail });
        const legDeltas = legs
          .map((leg) => leg.current_delta)
          .filter((value) => value !== null && value !== undefined);
        // Operation-level Net Delta: sum of each leg's already-computed current_delta
        // (same field shown per-leg in the Greeks grid). Null when no leg has delta data.
        const netDelta = legDeltas.length ? legDeltas.reduce((sum, value) => sum + Number(value), 0) : null;

        return {
          id: operation.id,
          strategyLabel: operation.strategy_label,
          status: operation.status,
          underlying: operation.underlying,
          expiration: operation.expiration,
          expirationLabel: operation.expiration_label,
          dte,
          openedAt: operation.opened_at,
          sizeDisplay: formatSizeDisplay(legs),
          netPnlNative: operation.net_pnl_base,
          netPnlUsd: operation.net_pnl_usd,
          netDelta,
          realizedPnlNative: operation.realized_pnl_base,
          realizedPnlUsd: operation.realized_pnl_usd,
          unrealizedPnlNative: operation.unrealized_pnl_base,
          unrealizedPnlUsd: operation.unrealized_pnl_usd,
          feesNative: operation.total_fees_base,
          feesUsd: operation.total_fees_usd,
          operation,
          legs,
          campaignDetail,
          campaignContext,
        };
      });

      setItems(normalized);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.failedLoadPositions'));
    } finally {
      setLoading(false);
    }
  }, [connected, t]);

  useEffect(() => {
    loadOpenPositions();
  }, [loadOpenPositions]);

  const filteredItems = useMemo(() => {
    const filtered = filterOpenPositions({ positions: items, selectedCurrency, underlyingFilter, strategyFilter });
    return sortOpenPositions({ positions: filtered, sortBy, selectedCurrency });
  }, [items, selectedCurrency, underlyingFilter, strategyFilter, sortBy]);

  const strategyOptions = useMemo(() => buildOpenPositionsStrategyOptions({
    positions: items,
    selectedCurrency,
    underlyingFilter,
  }), [items, selectedCurrency, underlyingFilter]);

  useEffect(() => {
    if (strategyFilter !== 'all' && !strategyOptions.includes(strategyFilter)) {
      setStrategyFilter('all');
    }
  }, [strategyFilter, strategyOptions]);

  const handleCurrencyChange = useCallback((currency) => {
    setSelectedCurrency(currency);
    if (currency === 'btc' || currency === 'eth') {
      setUnderlyingFilter(currency);
    }
  }, []);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      await syncOptionsAccount();
      toast.success(t('toasts.syncCompleted'));
      await Promise.all([reloadOverview?.(), loadOpenPositions()]);
      emitOptionsOpenOperationsUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.syncFailed'));
    } finally {
      setSyncing(false);
    }
  }, [loadOpenPositions, reloadOverview, t]);

  return {
    loading,
    syncing,
    items,
    filteredItems,
    selectedCurrency,
    setSelectedCurrency: handleCurrencyChange,
    underlyingFilter,
    setUnderlyingFilter,
    strategyFilter,
    setStrategyFilter,
    strategyOptions,
    sortBy,
    setSortBy,
    reloadOpenPositions: loadOpenPositions,
    handleSync,
  };
};

export default useOptionsOpenPositionsData;